/** 仅允许站内相对路径，避免 open redirect */
export function sanitizeH5BackPath(raw: string | null | undefined): string | undefined {
  const v = raw?.trim() ?? "";
  if (!v.startsWith("/") || v.startsWith("//")) {
    return undefined;
  }
  return v;
}

const LEGAL_BACK_STORAGE_KEY = "lf-h5-legal-back";

/**
 * 小程序 web-view 内 SPA 默认 push（与浏览器一致）；
 * 进法规页前写入返回路径，与 ?back= / location.state 互补。
 */
export function rememberLegalBackPath(back: string | undefined): void {
  const path = sanitizeH5BackPath(back);
  if (!path || typeof sessionStorage === "undefined") {
    return;
  }
  try {
    sessionStorage.setItem(LEGAL_BACK_STORAGE_KEY, path);
  } catch {
    /* private mode 等忽略 */
  }
}

export function readLegalBackPath(): string | undefined {
  if (typeof sessionStorage === "undefined") {
    return undefined;
  }
  try {
    return sanitizeH5BackPath(sessionStorage.getItem(LEGAL_BACK_STORAGE_KEY));
  } catch {
    return undefined;
  }
}

/**
 * H5 站内法律文档路径（相对路径，供 React Router / h5NavigateTo 使用）。
 * 必须始终相对：生产若拼成绝对 URL，小程序里 back 查询参数会被丢掉，返回键空转。
 * REACT_APP_LEGAL_WEB_ORIGIN 仅用于对外分享等绝对地址场景（见 h5LegalAbsoluteUrl）。
 */
export function h5LegalUrl(slug: string, options?: { back?: string }): string {
  const path = `/legal/${slug}`;
  const back = sanitizeH5BackPath(options?.back);
  if (!back) {
    return path;
  }
  const q = new URLSearchParams();
  q.set("back", back);
  return `${path}?${q.toString()}`;
}

/** 打开法规页：记忆返回路径 + 生成带 ?back= 的站内地址 */
export function openH5LegalPath(slug: string, back?: string): string {
  rememberLegalBackPath(back);
  return h5LegalUrl(slug, { back });
}

/**
 * 法规页解析返回地址：query → router state → sessionStorage → 首页兜底。
 * 有 history 时系统右滑会 pop；按钮仍走显式路径以保持稳定。
 */
export function resolveLegalBackPath(options: {
  queryBack?: string | null;
  stateBack?: unknown;
}): string {
  const fromQuery = sanitizeH5BackPath(options.queryBack);
  if (fromQuery) {
    return fromQuery;
  }
  const fromState = sanitizeH5BackPath(
    typeof options.stateBack === "string" ? options.stateBack : undefined
  );
  if (fromState) {
    return fromState;
  }
  return readLegalBackPath() || "/";
}

/** 法律文档绝对地址（分享 / 外链）；站内跳转请用 h5LegalUrl */
export function h5LegalAbsoluteUrl(slug: string): string {
  const base = process.env.REACT_APP_LEGAL_WEB_ORIGIN?.trim();
  if (base) {
    return `${base.replace(/\/$/, "")}/legal/${slug}`;
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/legal/${slug}`;
  }
  return `/legal/${slug}`;
}
